// Tap-to-focus: shared stateless helpers for the WIN Field PWA's in-app
// camera surfaces (field_photo_camera_controller.js card #3488,
// field_odometer_camera_controller.js card #4495).
//
// Both surfaces render a <video> element under a CSS `object-fit: cover`
// crop inside a positioned containing box, and both need the exact same two
// hardware-independent pieces to support tap-to-focus:
//
//   1. Translating a tap's viewport pixel coordinate into the normalised
//      {x, y} (0-1) point within the NATIVE video frame, accounting for the
//      object-fit: cover crop -- so the focus point lands where the tasker
//      actually tapped on screen, not where it would land on the uncropped
//      sensor frame.
//   2. A brief animated focus-ring shown at the tap point -- standard
//      camera-app tap-to-focus feedback.
//
// Hardware capability detection and applyConstraints() itself stay in
// camera_stream.js (focusCapabilityForTrack / applyFocusToTrack) -- the
// odometer controller doesn't use the CameraStream class, so it calls those
// exported functions directly against its own raw MediaStreamTrack instead
// of going through a CameraStream instance.

// Normalised {x, y} (0-1) focus point within the native video frame for a
// tap at (clientX, clientY) in viewport coordinates, given a <video> element
// currently rendered under `object-fit: cover`. Returns null when there's no
// live frame yet or the element has no rendered size.
export function focusPointFromTap(video, clientX, clientY) {
  const vw = video.videoWidth
  const vh = video.videoHeight
  if (!vw || !vh) return null

  const rect = video.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return null
  const fracX = (clientX - rect.left) / rect.width
  const fracY = (clientY - rect.top) / rect.height

  const target = rect.width / rect.height
  let sw, sh, sx, sy
  if (vw / vh > target) {
    // Source wider than the displayed box -- full height shown, sides cropped.
    sh = vh
    sw = vh * target
    sx = (vw - sw) / 2
    sy = 0
  } else {
    // Source taller/narrower than the displayed box -- full width shown, top/bottom cropped.
    sw = vw
    sh = vw / target
    sx = 0
    sy = (vh - sh) / 2
  }

  const x = (sx + fracX * sw) / vw
  const y = (sy + fracY * sh) / vh
  return { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) }
}

// Brief animated ring at the tap point, standard camera-app tap-to-focus
// feedback. Positioned relative to `container` (expected to be position:
// relative/absolute, e.g. the viewfinder's wrapping box) so it tracks the
// tap exactly regardless of layout. Pure Tailwind transition classes -- no
// new asset, no keyframes. Built with createElement, not innerHTML.
//
// Carries BOTH `field-camera-focus-ring` (the shared, surface-agnostic hook)
// and `field-photo-camera-focus-ring` (the original card #3488 class name,
// kept so the existing Playwright locator in field_camera_photo_local.spec.js
// keeps working unchanged after this helper moved here from the controller).
export function showFocusRing(container, clientX, clientY) {
  if (!container) return
  const rect = container.getBoundingClientRect()

  const ring = document.createElement("div")
  ring.className = "field-camera-focus-ring field-photo-camera-focus-ring pointer-events-none absolute z-20 size-16 -ml-8 -mt-8 rounded-lg border-2 border-white transition-all duration-500 ease-out opacity-100 scale-100"
  ring.style.left = `${clientX - rect.left}px`
  ring.style.top = `${clientY - rect.top}px`
  container.appendChild(ring)

  // Force layout so the class swap below actually transitions rather than
  // landing already-final.
  void ring.offsetWidth
  requestAnimationFrame(() => {
    ring.classList.remove("scale-100")
    ring.classList.add("scale-90", "opacity-0")
  })
  setTimeout(() => ring.remove(), 550)
}
